import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin, googleLogout } from "@react-oauth/google";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";

import { setAlertMessageRequest } from "store/ui/reducer";
import { loginRequest, logoutRequest, clearAuthError } from "store/user/reducer";
import { authErrorSelector, isLoadingUserSelector, userSelector } from "store/user/selector";
import { useAuthStyles } from "theme/hooks/useAuthStyles";

import { getRouteByRole, SignInFormInputs } from "../constants";

export const useSignInLogic = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector(userSelector);
  const isDoingLogin = useSelector(isLoadingUserSelector);
  const authError = useSelector(authErrorSelector);

  const [isFetchingProfile, setIsFetchingProfile] = useState(false);

  const authErrorStatus = typeof authError === "string" || !authError ? undefined : (authError as any).status;
  const authErrorMessage = typeof authError === "string" ? authError : (authError as any)?.message;
  const authErrorMessageText = typeof authErrorMessage === "string" ? authErrorMessage : "";

  const shouldShowServerError = useMemo(() => {
    return !!authErrorMessageText && (authErrorStatus === 400 || authErrorStatus === 429);
  }, [authErrorMessageText, authErrorStatus]);

  const [showError, setShowError] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(clearAuthError());
    };
  }, [dispatch]);

  useEffect(() => {
    setShowError(shouldShowServerError);
  }, [shouldShowServerError]);

  const isLoggedIn = !!localStorage.getItem("access_token");
  const expiresAt = Number(localStorage.getItem("expired_at")) * 1000;

  useEffect(() => {
    if (!isLoggedIn || expiresAt < Date.now()) {
      googleLogout();
      dispatch(logoutRequest());
    } else if (user) {
      navigate(getRouteByRole(user.user_role_type_id));
    }
  }, [dispatch, expiresAt, isLoggedIn, navigate, user]);

  const handleLogin = (payload: any) => {
    setShowError(false);
    dispatch(loginRequest(payload));
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsFetchingProfile(true);
        const { access_token } = tokenResponse;
        const url = `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${access_token}`;
        const { data } = await axios.get(url);

        if (data) {
          const { email, verified_email } = data;

          if (!verified_email) {
            setAlertMessageRequest({
              type: "error",
              message: "Your gmail account does not verified. please verify your gmail account.",
            });
            return;
          }

          handleLogin({ email, is_google_auth: true });
        }
      } catch (err: any) {
        console.log(err?.data?.message);
      } finally {
        setIsFetchingProfile(false);
      }
    },
  });

  const handleFormSubmit = async (values: SignInFormInputs, { validateForm }: any) => {
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      return;
    }
    handleLogin(values);
  };

  const isLoading = isFetchingProfile || isDoingLogin;

  const authStyles = useAuthStyles("customer");

  return {
    navigate,
    authStyles,
    user,
    isLoading,
    isDoingLogin,
    showError,
    authErrorMessageText,
    handleGoogleLogin,
    handleFormSubmit,
  };
};
